import './NewReferralButton.scss';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import React, { useEffect, useState } from 'react';
import { buildRoute, EMPTY_GUID, resrictWordsOnly, useDataFetcher } from 'Utilities';
import { KeyboardArrowDown, KeyboardArrowUp, Close } from '@mui/icons-material';
import { AddEditSubRoute, ROUTES } from 'Navigation';
import { AsyncStatus, InputType, NamedCoreEnum } from 'Models';
import { useHistory } from 'react-router-dom';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useGlobalContext } from 'Context';
import Dropdown, { DropdownDbOption, DropdownOption } from '../Form/Dropdown';
import { PrimaryButton, SecondaryButton } from './Buttons';
import bodySystem from 'Assets/Icons/body_system_dropdown.svg';
import cardiology from 'Assets/Icons/cardiology_dropdown.svg';
import stethoscope from 'Assets/Icons/stethoscope_dropdown.svg';
import conditions from 'Assets/Icons/conditions_dropdown.svg';
import heapSnapdshotLarge from 'Assets/Icons/heap_snapshot_large_dropdown.svg';
import badge from 'Assets/Icons/badge_dropdown.svg';
import pharmacy from 'Assets/Icons/local_pharmacy_dropdown.svg';
import { Render } from 'Components/Display';
import { InputActive } from 'v3/Components/Form/InputActive';

type LineOfBusinessEnumModel = {
  displayName: string;
  value: number;
};

export type ServiceModel = {};

export type SubServiceModel = {};

export type ReportQuestionModel = {};

export type LineOfBusinessModel = {
  lineOfBusiness: LineOfBusinessEnumModel;
  service: ServiceModel;
  subService: SubServiceModel;
  reportQuestion: ReportQuestionModel[];
};

type NewReferralButtonProps = {
  variant?: 'blue' | 'white' | undefined;
  text?: string;
  startIcon?: React.ReactNode;
};

const NewReferralButton: React.FC<NewReferralButtonProps> = ({ variant, text, startIcon }) => {
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const [allClients, clientStatus] = useDataFetcher(
    `${ENDPOINTS.clients.base}/dropdownItems`,
    [],
    ({ data }: any) => data,
    undefined,
    true
  );
  const clients = allClients.filter((client: DropdownDbOption) =>
    userdata?.clientIds?.includes(client.id)
  );
  const isInternal = userdata?.isInternal;
  const isClient = userdata?.isClient;
  const isSingleClient = userdata?.isClient && clients?.length === 1;
  const isManyClients: boolean =
    (userdata?.isClient && clients?.length > 1) || false;

  const history = useHistory();

  function navigateToNewReferral(
    lob: number,
    clientId?: string,
    referrerId?: string,
    otherReferrerName?: string
  ) {
    const route =
      buildRoute(
        ROUTES.ADD_EDIT,
        AddEditSubRoute.ReferralsRequest,
        EMPTY_GUID,
        ''
      ) +
      `?lob=${lob}` +
      (clientId ? `&clientId=${clientId}` : '') +
      (referrerId ? `&referrerId=${referrerId}` : '') +
      (otherReferrerName
        ? `&otherReferrerName=${encodeURIComponent(otherReferrerName)}`
        : '');
    history.push(route);
  }

  type NewReferralButtonProps = {
    text?: string;
    startIcon?: React.ReactNode;
  };

  const InternalNewReferralButton: React.FC<NewReferralButtonProps> = ({ text, startIcon }) => {
    const [client, setClient] = useState<DropdownOption>();
    const [referrer, setReferrer] = useState<DropdownOption>();
    const [lob, setLob] = useState<DropdownOption>();
    const [buttonEnabled, setButtonEnabled] = useState(false);
    const [otherReferrerName, setOtherReferrerName] = useState<string>('');

    React.useEffect(() => {
      if (client?.id && lob?.value && referrer?.id && (referrer?.displayName !== 'Other' || (referrer?.displayName === 'Other' && (otherReferrerName.trim().length > 2 && otherReferrerName.trim().length < 51)))) {
        setButtonEnabled(true);
      } else {
        setButtonEnabled(false);
      }
    }, [client, referrer, lob, otherReferrerName]);

    React.useEffect(() => {
      if (client) {
        setReferrer(undefined);
        setLob(undefined);
      }
    }, [client]);

    React.useEffect(() => {
      if (referrer?.displayName === 'Other') {
        setOtherReferrerName('');
      }
    }, [referrer]);

    const [clientLobs, clientLobsStatus] = useDataFetcher<NamedCoreEnum>(
      `${ENDPOINTS.users.base}/LineOfBusinessbyClient?clientId=${client?.id}`,
      {},
      // transform the data to the format expected by the dropdown
      ({ data }: any) => {
        return data.map((lob: LineOfBusinessModel) => ({
          value: lob.lineOfBusiness.value,
          displayName: lob.lineOfBusiness.displayName,
        }));
      },
      undefined,
      client?.id !== undefined,
      '3.0'
    );

    const [clientUsers, clientUsersStatus] = useDataFetcher<DropdownDbOption[]>(
      `${ENDPOINTS.users.base}/DropdownItems?clientId=${client?.id}&isClient=true`,
      [],
      ({ data }: any) => data,
      undefined,
      client?.id !== undefined
    );

    const [dialogVisible, setDialogVisible] = React.useState<boolean>(false);

    const toggleDialog = () => {
      setDialogVisible(!dialogVisible);
    };

    useEffect(() => {
      const setUserIdAsReferrer = () => {
        if (userdata?.id) {
          const userOption = clientUsers.find(
            (user) => user.id === userdata.id
          );
          if (userOption) {
            setReferrer({
              id: userOption.id,
              value: userOption.id,
              displayName: userOption.name,
            });
          }
        }
      };
      if (clientUsers.length > 0 && isManyClients) {
        setUserIdAsReferrer();
      }
    }, [clientUsers, client, isManyClients]);

    function StartNewInternalReferral() {
      navigateToNewReferral(lob?.value as number, client?.id, referrer?.id, otherReferrerName);
    }

    return (
      <div className="proxied-by-wrapper">
        {/* if variant is white then use secondary button */}
        {variant === 'white' ? (
          <SecondaryButton className="centered" onClick={toggleDialog}>
            {startIcon && <span className="btn-start-icon">{startIcon}</span>}
            <div className="title">{text ?? 'New Referral'}</div>
          </SecondaryButton>
        ) : (
          <PrimaryButton className="centered" onClick={toggleDialog}>
            {startIcon && <span className="btn-start-icon">{startIcon}</span>}
            <div className="title">{text ?? 'New Referral'}</div>
          </PrimaryButton>
        )}
        {dialogVisible && (
          <Dialog className="new-referral-dialog" onClose={toggleDialog}>
            <div className="dialog-header">
              <div className="dialog-title">
                Which client and line of business is this referral for?
              </div>
              <div className="dialog-close" onClick={toggleDialog}>
                <span className="dialog-close-icon">
                  <Close />
                </span>
              </div>
            </div>
            <div className="dialog-row">
              <div className="dialog-column">
                <p>Please select the client this referral is for.</p>
              </div>
            </div>

            <div className="dialog-row">
              <div className="dialog-column">
                {clientStatus === AsyncStatus.Success && (
                  <Dropdown
                    label="Client Name"
                    value={client}
                    onChange={(value) => {
                      setClient(value);
                    }}
                    dbOptions={clients}
                    required
                  />
                )}
              </div>
              <div className="dialog-column">
                <Dropdown
                  label="Referrer"
                  value={referrer}
                  disabled={isManyClients}
                  onChange={(value) => {
                    setReferrer(value);
                  }}
                  dbOptions={
                    clientUsersStatus === AsyncStatus.Success ? clientUsers : []
                  }
                  sortAlphabetically
                  required={true}
                />
              </div>
            </div>
            <div className="dialog-row">
              <Render condition={referrer?.displayName === "Other"}>
                <div className="dialog-column">
                  <InputActive
                    id='OtherReferrerName'
                    label="Other Referrer Name"
                    required
                    value={otherReferrerName}
                    onChange={(event) => {
                      const inputValue = (event.target as HTMLInputElement).value;
                      setOtherReferrerName(inputValue);
                    }}
                    restrictValues={(value) => resrictWordsOnly(value)}
                    error={otherReferrerName.trim().length > 2 && otherReferrerName.trim().length < 51 ? '' : 'Please enter a valid other referrer name.'}
                  />
                </div>
              </Render>
              <div className="dialog-column">
                <Dropdown
                  label="Line of Business"
                  value={lob}
                  onChange={(value) => {
                    setLob(value);
                  }}
                  enums={
                    clientLobsStatus === AsyncStatus.Success ? clientLobs : {}
                  }
                  required
                  sortAlphabetically
                />
              </div>
            </div>
            <div className="dialog-footer">
              <PrimaryButton
                className={`centered ${buttonEnabled ? '' : 'disabled'}`}
                onClick={StartNewInternalReferral}
              >
                <div className="title">Start Referral</div>
              </PrimaryButton>
            </div>
          </Dialog>
        )}
      </div>
    );
  };

  const ClientNewReferralButton: React.FC<NewReferralButtonProps> = ({ text, startIcon }) => {
    const [hasOneLob, setHasOneLob] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [linesOfBusiness, fetchStatus] = useDataFetcher<
      LineOfBusinessModel[]
    >(
      `${ENDPOINTS.users.base}/lineOfBusiness`,
      [],
      ({ data }: any) => data,
      undefined,
      true,
      '3.0'
    );
    const iconMap: Record<string, string> = {
      IME: conditions,
      Pharmacy: pharmacy,
      Disability: bodySystem,
      'Health Review': cardiology,
      'Impairment Rating Review': heapSnapdshotLarge,
      'Workers Comp - Peer Review': badge,
      "Worker's Comp": badge,
    };

    useEffect(() => {
      if (linesOfBusiness?.length === 1) {
        setHasOneLob(true);
      }
    }, [linesOfBusiness]);

    const toggleDropdown = () => {
      setIsOpen((prev) => !prev);
    };

    const NewReferralDropdown = () => {
      return (
        <div className="buttonWrapper">
          {/* if variant is white then use secondary button */}
          {variant === 'white' ? (
            <SecondaryButton
              onClick={toggleDropdown}
              className={`${isOpen ? 'open' : ''}`}
            >
              {startIcon && <span className="btn-start-icon">{startIcon}</span>}
              <div className="title">{text ?? 'New Referral'}</div>
              <span className={`${variant === 'white' ? 'arrowIcon-blue' : 'arrowIcon'}`}>
                {!isOpen ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
              </span>
            </SecondaryButton>
          ) : (
            <PrimaryButton
              onClick={toggleDropdown}
              className={`${isOpen ? 'open' : ''}`}
            >
              {startIcon && <span className="btn-start-icon">{startIcon}</span>}
              <div className="title">{text ?? 'New Referral'}</div>
              <span className='arrowIcon'>
                {!isOpen ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
              </span>
            </PrimaryButton>
          )}
          {isOpen && (
            <div>
              <ul className="newReferralList">
                {linesOfBusiness?.map((lob) => (
                  <li
                    key={lob.lineOfBusiness.value}
                    onClick={() =>
                      navigateToNewReferral(
                        lob.lineOfBusiness.value,
                        clients[0]?.id
                      )
                    }
                  >
                    {iconMap[lob.lineOfBusiness.displayName as string] && (
                      <img
                        src={iconMap[lob.lineOfBusiness.displayName as string]}
                        alt=""
                        className="dropdown-option-icon"
                        style={{ marginRight: 8, width: 18, height: 18 }}
                      />
                    )}
                    <span className="linkText">
                      {lob.lineOfBusiness.displayName}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    };

    const SingleLobButton = () => {
      return (
        <PrimaryButton
          className="centered"
          onClick={() =>
            navigateToNewReferral(
              linesOfBusiness[0].lineOfBusiness.value,
              clients[0]?.id
            )
          }
        >
          {startIcon && <span className="btn-start-icon">{startIcon}</span>}
          <div className="title">{text ?? 'New Referral'}</div>
        </PrimaryButton>
      );
    };

    return (
      <>
        {fetchStatus === AsyncStatus.Success &&
          (hasOneLob ? <SingleLobButton /> : <NewReferralDropdown />)}
      </>
    );
  };

  return (
    <>
      {isInternal || isManyClients ? (
        <InternalNewReferralButton text={text} startIcon={startIcon} />
      ) : isSingleClient ? (
        <ClientNewReferralButton text={text} startIcon={startIcon} />
      ) : (
        <></>
      )}
    </>
  );
};

export default NewReferralButton;
